import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2,
  FileDown, Receipt, Link2, CheckCircle, Send, Clock,
  ChevronDown, RefreshCw,
} from 'lucide-react';
import { formatCurrency, LOGO_URL, LOGO_FALLBACK_URL, fetchPaymentDefaults } from '../lib/defaultData';
import { BRAND_NAME, BRAND_ACN, fetchCompanyDefaults } from '../lib/brandConfig';
import { generateInvoicePDF } from '../lib/invoicePdfExport';
import { ExportDropdown } from '../components/shared/PageActions';
import ClientSelector from '../components/quote/ClientSelector';
import ContactSelector from '../components/quote/ContactSelector';

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Defaults ──────────────────────────────────────────────────────────────────
// Payment defaults are fetched from backend API via fetchPaymentDefaults()
// S-02: company address/phone/director are also fetched from backend
const DEFAULT_COMPANY_INFO = {
  name: BRAND_NAME,
  acn: BRAND_ACN,
  address: '',
  phone: '',
  director: '',
  logo_url: '',
};

async function fetchCompanyInfoDefaults() {
  const info = { ...DEFAULT_COMPANY_INFO };
  try {
    const cd = await fetchCompanyDefaults();
    info.address = cd.company_address || '';
    info.phone = cd.company_phone || '';
    info.director = cd.company_director || '';
  } catch (_) {}
  return info;
}

function makeBlankInvoice(paymentDefaults = {}) {
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return {
    invoice_no: '',
    invoice_date: today,
    due_date: due,
    quote_id: '',
    quote_no: '',
    client_name: '',
    contact_person: '',
    client_abn: '',
    client_address: '',
    project_name: '',
    project_address: '',
    company_info: { ...DEFAULT_COMPANY_INFO },
    items: [{ id: newId(), description: '', amount: 0 }],
    gst_enabled: true,
    notes: '',
    status: 'draft',
    payment_details: { ...paymentDefaults },
  };
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft',  Icon: Clock,       bg: '#f3f2ef', color: '#6b7280' },
  { value: 'sent',  label: 'Sent',   Icon: Send,        bg: '#eff6ff', color: '#2563eb' },
  { value: 'paid',  label: 'Paid',   Icon: CheckCircle, bg: '#f0fdf4', color: '#16a34a' },
];

function Section({ title, children, extra }) {
  return (
    <div
      className="bg-white rounded-lg shadow-sm p-6"
      style={{ border: '1px solid rgba(201,168,76,0.12)' }}
    >
      {title && (
        <div className="flex items-center justify-between mb-4" style={{ borderBottom: '1px solid rgba(201,168,76,0.2)', paddingBottom: '8px' }}>
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#c9a84c' }}>
            {title}
          </h3>
          {extra}
        </div>
      )}
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-xs font-semibold uppercase tracking-wider text-[#6b7280] mb-1">{children}</label>;
}

// ── Quote Picker dropdown ─────────────────────────────────────────────────────
function QuotePicker({ quotes, selectedId, onSelect, loading }) {
  return (
    <div className="relative">
      <div className="relative">
        <select
          value={selectedId || ''}
          onChange={e => onSelect(e.target.value)}
          disabled={loading}
          className="w-full appearance-none border border-[rgba(201,168,76,0.3)] rounded-md px-3 py-2.5 pr-10 text-sm bg-white focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
          style={{ color: selectedId ? '#1a1a2e' : '#9ca3af' }}
        >
          <option value="">— Select a quotation to load data —</option>
          {quotes.map(q => (
            <option key={q.id} value={q.id}>
              {q.quote_no || '(no number)'}
              {q.client_name ? `  |  ${q.client_name}` : ''}
              {q.project_name ? `  |  ${q.project_name}` : ''}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280] pointer-events-none" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function InvoiceEditor() {
  const { id } = useParams();                        // undefined = new invoice
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When opened from a project, return there instead of the global invoices list
  const fromProject = searchParams.get('from_project');
  const backTo = fromProject ? `/projects/${fromProject}` : '/?tab=invoices';
  const backLabel = fromProject ? 'Project' : 'Invoices';

  const [invoiceData, setInvoiceData]   = useState(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [isSaving, setIsSaving]         = useState(false);
  const [focusedAmountId, setFocusedAmountId] = useState(null);

  // For the "Load from Quote" picker
  const [quotes, setQuotes]             = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [loadingFromQuote, setLoadingFromQuote] = useState(false);

  // ── Fetch available quotes for picker ───────────────────────────────────────
  useEffect(() => {
    const fetchQuotes = async () => {
      setQuotesLoading(true);
      try {
        const res = await axios.get(`${API}/quotes`);
        setQuotes(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Failed to fetch quotes:', err);
      }
      setQuotesLoading(false);
    };
    fetchQuotes();
  }, []);

  // ── Load invoice or init blank ───────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (isNew) {
        // New invoice — initialize with defaults + fetch next number + fresh brand
        const paymentDefaults = await fetchPaymentDefaults();
        const blank = makeBlankInvoice(paymentDefaults);
        const { refreshCompanyBrand } = await import('../lib/brandConfig');
        const brand = await refreshCompanyBrand();
        blank.company_info = {
          ...DEFAULT_COMPANY_INFO,
          name: brand?.name || '',
          acn: brand?.acn || '',
          logo_url: brand?.logo_url || '',
          phone: brand?.phone || '',
          director: brand?.director || '',
          address: brand?.address || '',
          website: brand?.website || '',
          email: brand?.email || '',
        };
        try {
          const numRes = await axios.get(`${API}/invoices/next-number`);
          blank.invoice_no = numRes.data.invoice_no;
        } catch (err) {
          console.error('Failed to get next invoice number:', err);
          toast.error('Could not fetch invoice number — check backend is running');
        }
        setInvoiceData(blank);
        setIsLoading(false);
        return;
      }
      // Existing invoice — load from API, always merge fresh brand
      try {
        const res = await axios.get(`${API}/invoices/${id}`);
        const d = res.data;
        d.items = (d.items || []).map(it => ({ ...it, id: it.id || newId() }));
        const ci = d.company_info || {};
        const { refreshCompanyBrand } = await import('../lib/brandConfig');
        const brand = await refreshCompanyBrand();
        d.company_info = {
          ...DEFAULT_COMPANY_INFO,
          ...ci,
          name: brand?.name || ci.name || '',
          acn: brand?.acn || ci.acn || '',
          logo_url: brand?.logo_url || ci.logo_url || '',
          phone: brand?.phone || ci.phone || '',
          director: brand?.director || ci.director || '',
          address: brand?.address || ci.address || '',
          website: brand?.website || ci.website || '',
          email: brand?.email || ci.email || '',
        };
        setInvoiceData(d);
      } catch (err) {
        console.error('Failed to load invoice:', err);
        toast.error('Failed to load invoice');
      }
      setIsLoading(false);
    };
    load();
  }, [id, isNew]);

  // ── Sync company_info when brand is updated in Settings ────────────────────
  useEffect(() => {
    const handler = async () => {
      const { refreshCompanyBrand } = await import('../lib/brandConfig');
      const brand = await refreshCompanyBrand();
      if (!brand) return;
      setInvoiceData(prev => prev ? {
        ...prev,
        company_info: {
          ...prev.company_info,
          name: brand.name || '',
          acn: brand.acn || '',
          logo_url: brand.logo_url || '',
          phone: brand.phone || prev.company_info?.phone || '',
          director: brand.director || prev.company_info?.director || '',
          website: brand.website || prev.company_info?.website || '',
          email: brand.email || prev.company_info?.email || '',
          address: brand.address || prev.company_info?.address || '',
        }
      } : prev);
    };
    window.addEventListener('company-brand-updated', handler);
    return () => window.removeEventListener('company-brand-updated', handler);
  }, []);

  // ── Load data from a selected quote ─────────────────────────────────────────
  const handleLoadFromQuote = useCallback(async (quoteId) => {
    if (!quoteId) {
      // Cleared — remove quote link but keep other data
      setInvoiceData(prev => prev ? { ...prev, quote_id: '', quote_no: '' } : prev);
      return;
    }
    setLoadingFromQuote(true);
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}`);
      const q = res.data;

      // Convert Quote categories → Invoice line items
      const categories = q.categories || [];
      const lineItems = categories.length > 0
        ? categories.map(cat => ({
            id: cat.id || newId(),
            description: cat.name || 'Unnamed Category',
            amount: Number(cat.price) || 0,
          }))
        : [{ id: newId(), description: '', amount: 0 }];

      setInvoiceData(prev => ({
        ...prev,
        quote_id:        q.id || '',
        quote_no:        q.quote_no || '',
        client_name:     q.client_name || prev.client_name,
        contact_person:  q.contact_person || prev.contact_person,
        client_abn:      q.client_abn || prev.client_abn,
        client_address:  q.client_address || prev.client_address,
        client_id:       q.client_id || prev.client_id || '',
        project_name:    q.project_name || prev.project_name,
        project_address: q.project_address || prev.project_address,
        company_info:    q.company_info || prev.company_info,
        payment_details: q.payment_details || prev.payment_details,
        items:           lineItems,
        gst_enabled:     q.gst_enabled ?? prev.gst_enabled,
      }));
      toast.success(`Loaded data from ${q.quote_no || 'quote'}`);
    } catch (err) {
      console.error('Failed to load quote:', err);
      toast.error('Failed to load quote data');
    }
    setLoadingFromQuote(false);
  }, []);

  // ── Field updaters ──────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setInvoiceData(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updatePaymentDetails = useCallback((field, value) => {
    setInvoiceData(prev => prev ? {
      ...prev,
      payment_details: { ...prev.payment_details, [field]: value },
    } : prev);
  }, []);

  // ── Line items ──────────────────────────────────────────────────────────────
  const addItem = useCallback(() => {
    setInvoiceData(prev => prev ? {
      ...prev,
      items: [...prev.items, { id: newId(), description: '', amount: 0 }],
    } : prev);
  }, []);

  const updateItem = useCallback((itemId, field, value) => {
    setInvoiceData(prev => prev ? {
      ...prev,
      items: prev.items.map(it => it.id === itemId ? { ...it, [field]: value } : it),
    } : prev);
  }, []);

  const removeItem = useCallback((itemId) => {
    setInvoiceData(prev => prev ? {
      ...prev,
      items: prev.items.filter(it => it.id !== itemId),
    } : prev);
  }, []);

  // ── Computed totals ─────────────────────────────────────────────────────────
  const subtotal = useMemo(() =>
    (invoiceData?.items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0),
    [invoiceData?.items]
  );
  const gstAmount = invoiceData?.gst_enabled ? subtotal * 0.1 : 0;
  const total     = subtotal + gstAmount;

  // ── Save ────────────────────────────────────────────────────────────────────
  const saveInvoice = useCallback(async () => {
    if (!invoiceData || isSaving) return;
    setIsSaving(true);
    try {
      // Strip internal fields before sending
      const { id: _id, created_at: _ca, updated_at: _ua, subtotal: _sub, ...rest } = invoiceData;
      const payload = { ...rest };

      let saved;
      if (!isNew) {
        const res = await axios.put(`${API}/invoices/${id}`, payload);
        saved = res.data;
        toast.success('Invoice updated');
      } else {
        const res = await axios.post(`${API}/invoices`, payload);
        saved = res.data;
        toast.success('Invoice saved');
        navigate(
          fromProject ? `/invoice/${saved.id}?from_project=${fromProject}` : `/invoice/${saved.id}`,
          { replace: true }
        );
      }
    } catch (err) {
      console.error('Failed to save invoice:', err);
      toast.error('Failed to save invoice');
    }
    setIsSaving(false);
  }, [invoiceData, id, isNew, isSaving, navigate, fromProject]);

  // ── Export ──────────────────────────────────────────────────────────────────
  const handlePdfPreview = useCallback(() => {
    if (!invoiceData) return;
    generateInvoicePDF(invoiceData, subtotal, gstAmount, total, 'preview');
  }, [invoiceData, subtotal, gstAmount, total]);
  const handlePdfDownload = useCallback(() => {
    if (!invoiceData) return;
    generateInvoicePDF(invoiceData, subtotal, gstAmount, total, 'download');
  }, [invoiceData, subtotal, gstAmount, total]);
  const handlePrint = () => window.print();

  // ─────────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#e5e4df' }}>
        <p className="text-[#6b7280]">Invoice not found.</p>
        <Button onClick={() => navigate('/?tab=invoices')} variant="outline">
          Back to Invoices
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#e5e4df' }}>

      {/* ── Toolbar ── */}
      <div
        className="sticky top-0 z-40 no-print backdrop-blur-sm"
        style={{ background: 'rgba(26,26,46,0.97)', borderBottom: '2px solid rgba(201,168,76,0.3)' }}
      >
        <div className="max-w-[860px] mx-auto px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(backTo)}
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </button>

          <div className="h-5 w-px mx-1" style={{ background: 'rgba(201,168,76,0.4)' }} />

          <div className="flex items-center gap-2 text-white/50 text-sm">
            <Receipt className="w-4 h-4 text-[#c9a84c]" />
            <span className="font-mono font-semibold text-white">
              {invoiceData.invoice_no || 'New Invoice'}
            </span>
          </div>

          {invoiceData.quote_no && (
            <>
              <div className="h-5 w-px mx-1" style={{ background: 'rgba(201,168,76,0.4)' }} />
              <div className="flex items-center gap-1.5 text-white/50 text-xs">
                <Link2 className="w-3.5 h-3.5" />
                <span>Quote: {invoiceData.quote_no}</span>
              </div>
            </>
          )}

          <div className="flex-1" />

          {/* Status selector */}
          <div className="flex items-center gap-1 rounded-full px-1 py-1" style={{ background: 'rgba(255,255,255,0.08)' }}>
            {STATUS_OPTIONS.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => updateField('status', value)}
                className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  background: invoiceData.status === value ? 'rgba(201,168,76,0.25)' : 'transparent',
                  color: invoiceData.status === value ? '#c9a84c' : 'rgba(255,255,255,0.4)',
                }}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>

          <Button
            onClick={saveInvoice}
            disabled={isSaving}
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
            size="sm"
          >
            {isSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {isNew ? 'Save' : 'Update'}
          </Button>

          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-[860px] mx-auto px-6 py-8 space-y-5">

        {/* Company Header */}
        <div className="rounded-lg p-6 flex items-center gap-5" style={{ background: '#1a1a2e' }}>
          <img
            src={invoiceData.company_info?.logo_url || LOGO_URL}
            alt="Logo"
            style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            onError={(e) => {
              if (e.target.src !== LOGO_FALLBACK_URL) e.target.src = LOGO_FALLBACK_URL;
              else e.target.style.display = 'none';
            }}
          />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">{invoiceData.company_info?.name || BRAND_NAME}</h2>
            <p className="text-white/50 text-sm">ACN {invoiceData.company_info?.acn || ''}</p>
            {invoiceData.company_info?.phone && (
              <p className="text-white/50 text-sm">MOB: {invoiceData.company_info.phone}</p>
            )}
            {invoiceData.company_info?.address && (
              <p className="text-white/50 text-sm">{invoiceData.company_info.address}</p>
            )}
          </div>
          <div
            className="text-3xl font-black tracking-widest"
            style={{ color: '#c9a84c', letterSpacing: '0.15em' }}
          >
            INVOICE
          </div>
        </div>

        {/* ── Link to Quotation ── */}
        <Section
          title="Link to Quotation"
          extra={
            invoiceData.quote_id ? (
              <button
                onClick={() => navigate(`/quote/${invoiceData.quote_id}`)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md transition-colors"
                style={{ color: '#16a34a', background: '#f0fdf4' }}
              >
                <Link2 className="w-3 h-3" />
                {invoiceData.quote_no}
              </button>
            ) : null
          }
        >
          <p className="text-xs text-[#9ca3af] mb-3">
            Select a quotation to auto-fill client info, project details and bank account. Existing fields will be overwritten.
          </p>
          <div className="flex gap-2">
            <div className="flex-1">
              <QuotePicker
                quotes={quotes}
                selectedId={invoiceData.quote_id}
                onSelect={handleLoadFromQuote}
                loading={quotesLoading}
              />
            </div>
            {invoiceData.quote_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLoadFromQuote(invoiceData.quote_id)}
                disabled={loadingFromQuote}
                title="Reload from quote"
                className="shrink-0"
              >
                {loadingFromQuote
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />
                }
              </Button>
            )}
          </div>
        </Section>

        {/* Invoice Details */}
        <Section title="Invoice Details">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <FieldLabel>Invoice Number</FieldLabel>
              <Input
                value={invoiceData.invoice_no}
                onChange={e => updateField('invoice_no', e.target.value)}
                className="font-mono"
                placeholder="INV-0000-001"
              />
            </div>
            <div>
              <FieldLabel>Invoice Date</FieldLabel>
              <Input
                type="date"
                value={invoiceData.invoice_date}
                onChange={e => updateField('invoice_date', e.target.value)}
              />
            </div>
            <div>
              <FieldLabel>Due Date</FieldLabel>
              <Input
                type="date"
                value={invoiceData.due_date}
                onChange={e => updateField('due_date', e.target.value)}
              />
            </div>
          </div>
        </Section>

        {/* Client & Project */}
        <Section title="Client & Project">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Client Name</FieldLabel>
              <ClientSelector
                value={invoiceData.client_name}
                clientId={invoiceData.client_id}
                onChange={v => updateField('client_name', v)}
                onClientSelect={(client) => {
                  updateField('client_abn', client.abn || '');
                  updateField('client_address', client.address || '');
                  updateField('client_id', client.id || '');
                }}
                onClear={() => updateField('client_id', '')}
                placeholder="Search or type client name..."
                testId="invoice-client-name"
              />
            </div>
            <div>
              <FieldLabel>Contact Person</FieldLabel>
              <ContactSelector
                value={invoiceData.contact_person || ''}
                clientId={invoiceData.client_id}
                onChange={v => updateField('contact_person', v)}
                onContactSelect={(contact) => {
                  // Could auto-fill email/phone in future
                }}
                onClear={() => {}}
                placeholder="Search or type contact name..."
                testId="invoice-contact-person"
              />
            </div>
            <div>
              <FieldLabel>Client ABN</FieldLabel>
              <Input
                value={invoiceData.client_abn}
                onChange={e => updateField('client_abn', e.target.value)}
                placeholder="ABN"
              />
            </div>
            <div>
              <FieldLabel>Client Business Address</FieldLabel>
              <Input
                value={invoiceData.client_address || ''}
                onChange={e => updateField('client_address', e.target.value)}
                placeholder="Client business address"
              />
            </div>
            <div>
              <FieldLabel>Project Name</FieldLabel>
              <Input
                value={invoiceData.project_name}
                onChange={e => updateField('project_name', e.target.value)}
                placeholder="Project name"
              />
            </div>
            <div>
              <FieldLabel>Project Address</FieldLabel>
              <Input
                value={invoiceData.project_address}
                onChange={e => updateField('project_address', e.target.value)}
                placeholder="Project address"
              />
            </div>
          </div>
        </Section>

        {/* Line Items */}
        <Section title="Line Items">
          <div className="space-y-2">
            <div className="grid gap-3 px-1" style={{ gridTemplateColumns: '1fr 150px 36px' }}>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#6b7280]">Description</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#6b7280] text-right">Amount (excl. GST)</span>
              <span />
            </div>

            {invoiceData.items.map((item, idx) => (
              <div key={item.id} className="grid gap-3 items-center" style={{ gridTemplateColumns: '1fr 150px 36px' }}>
                <Input
                  value={item.description}
                  onChange={e => updateItem(item.id, 'description', e.target.value)}
                  placeholder={`Item ${idx + 1} description`}
                />
                <Input
                  className="text-right font-mono"
                  value={
                    focusedAmountId === item.id
                      ? (item.amount === 0 ? '' : item.amount)
                      : Number(item.amount || 0).toFixed(2)
                  }
                  onChange={e => updateItem(item.id, 'amount', e.target.value)}
                  onFocus={() => setFocusedAmountId(item.id)}
                  onBlur={() => {
                    setFocusedAmountId(null);
                    updateItem(item.id, 'amount', parseFloat(item.amount) || 0);
                  }}
                  placeholder="0.00"
                />
                <button
                  onClick={() => removeItem(item.id)}
                  className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"
                  disabled={invoiceData.items.length <= 1}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            <button
              onClick={addItem}
              className="flex items-center gap-1.5 text-sm text-[#c9a84c] hover:text-[#b8973e] mt-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>
        </Section>

        {/* Price Summary */}
        <Section title="Summary">
          <div className="max-w-xs ml-auto space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#6b7280]">Subtotal</span>
              <span className="font-mono font-medium">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <label className="flex items-center gap-2 cursor-pointer text-[#6b7280]">
                <input
                  type="checkbox"
                  checked={invoiceData.gst_enabled}
                  onChange={e => updateField('gst_enabled', e.target.checked)}
                  className="accent-[#c9a84c]"
                />
                GST (10%)
              </label>
              <span className="font-mono text-[#6b7280]">
                {invoiceData.gst_enabled ? formatCurrency(gstAmount) : '—'}
              </span>
            </div>
            <div
              className="flex justify-between text-base font-bold pt-3"
              style={{ borderTop: '2px solid #1a1a2e' }}
            >
              <span>Total</span>
              <span className="font-mono" style={{ color: '#c9a84c' }}>{formatCurrency(total)}</span>
            </div>
          </div>
        </Section>

        {/* Payment Details */}
        <Section title="Payment Details">
          <p className="text-xs text-[#9ca3af] mb-4">
            These bank account details appear on the invoice PDF. They are automatically inherited from the linked quotation.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel>Bank Name</FieldLabel>
              <Input
                value={invoiceData.payment_details?.bank_name || ''}
                onChange={e => updatePaymentDetails('bank_name', e.target.value)}
                placeholder="Bank name"
              />
            </div>
            <div>
              <FieldLabel>Account Name</FieldLabel>
              <Input
                value={invoiceData.payment_details?.account_name || ''}
                onChange={e => updatePaymentDetails('account_name', e.target.value)}
                placeholder="Account name"
              />
            </div>
            <div>
              <FieldLabel>BSB</FieldLabel>
              <Input
                value={invoiceData.payment_details?.bsb || ''}
                onChange={e => updatePaymentDetails('bsb', e.target.value)}
                placeholder="000-000"
              />
            </div>
            <div>
              <FieldLabel>Account Number</FieldLabel>
              <Input
                value={invoiceData.payment_details?.account_number || ''}
                onChange={e => updatePaymentDetails('account_number', e.target.value)}
                placeholder="Account number"
              />
            </div>
          </div>
        </Section>

        {/* Notes */}
        <Section title="Notes">
          <Textarea
            value={invoiceData.notes}
            onChange={e => updateField('notes', e.target.value)}
            placeholder="Additional notes for this invoice..."
            rows={3}
            className="resize-none"
          />
        </Section>
      </div>

    </div>
  );
}
